import { ReactCodeMirrorRef } from "@uiw/react-codemirror"
import TextCodeFieldInput from "app/codemirror/components/TextCodeField"
import { AutocompleteFunction } from "app/codemirror/types"
import { FormError } from "app/core/components/alerts/FormError"
import { dashedToTitle } from "app/paywalls/components/forms/editor/utils"
import { FocusEventHandler, forwardRef, PropsWithoutRef, Ref, useEffect, useRef } from "react"
import Textarea from "react-expanding-textarea"
import { FieldInputProps, useField } from "react-final-form"
import tw from "tailwind-styled-components"
import { STooltip } from "@superwall-me/sui"
import { useEnhancedField } from "./DisabledContext"
import moment from "moment"

export enum LabeledTextFieldType {
  NORMAL,
  MULTILINE,
  CODE,
}

export interface LabeledTextFieldProps extends PropsWithoutRef<JSX.IntrinsicElements["input"]> {
  /** Field name. */
  name: string
  /** Field label. */
  label: string
  submitOnEnter?: boolean
  /* Tooltip text */
  helpText?: string
  /* Warning Tooltip text */
  warningText?: string
  /** Field type. Doesn't include radio buttons and checkboxes */
  type?: "text" | "password" | "email" | "number"
  outerProps?: PropsWithoutRef<JSX.IntrinsicElements["div"]>
  nullOnEmpty?: boolean
  /** Type of the Input */
  inputType?: LabeledTextFieldType
  /** A function to help with autocompletes for Code Type Inputs **/
  autoCompeteFunction?: AutocompleteFunction
  disabled?: boolean
  /** Use this to make the label flush with the input (aka no padding-left) */
  isFlushLabel?: boolean
  toolTipWidth?: string
  value?: string
  isBoldWhiteLabel?: boolean
}

export const FieldLabel = tw.label<{ $isError?: boolean }>`
  ${({ $isError }) => {
    const textStyle = $isError ? "text-red-800" : "text-white text-opacity-60"
    return `
      block
      pl-1
      ${textStyle}
      text-sm
    `
  }}
`

export const StyledInput = tw.input`
  appearance-none
  shadow-none
  rounded-md
  border
  border-white
  border-opacity-10
  focus:outline-0
  focus:border-solid
  focus:border-opacity-25
  hover:border-opacity-25
  bg-grayscale-lightest bg-opacity-10
  py-2
  px-3
  w-full
  text-white
`

export const ButtonStyledInput = tw.input`
appearance-none
rounded-lg
py-2
px-2
mb-2
text-brand-primary
bg-grayscale-darker
border
border-white
border-opacity-7
focus:outline-none
focus-visible:ring-0
focus-visible:ring-white
focus-visible:ring-opacity-10
focus-visible:border-opacity-20
text-center
font-medium
`

const TextAreaStyle = `
appearance-none
rounded-lg
mb-3
w-full
py-2
px-3
text-white
bg-grayscale-light
focus:outline-none
border-0
`

export const LabeledOrValueTextFieldInputElement = forwardRef<
  HTMLInputElement | ReactCodeMirrorRef,
  {
    inputType: LabeledTextFieldType
    shouldDisable: boolean
    placeholderFromField?: string
    submitOnEnter?: boolean
    placeholderInput?: FieldInputProps<any, HTMLElement>
    autoCompeteFunction?: AutocompleteFunction
    disabled: boolean
    input: FieldInputProps<any, HTMLElement>
    props: Partial<LabeledTextFieldProps>
    label?: string
    prefix?: string
    onFocus?: () => void
    onBlur?: FocusEventHandler<HTMLTextAreaElement | HTMLInputElement>
  }
>(
  (
    {
      inputType,
      input,
      disabled,
      label,
      props,
      autoCompeteFunction,
      placeholderFromField,
      placeholderInput,
      shouldDisable,
      prefix,
      onFocus,
      onBlur,
      submitOnEnter = true,
    },
    ref
  ) => {
    switch (inputType) {
      case LabeledTextFieldType.MULTILINE:
        return (
          //@ts-ignore
          <Textarea
            style={{
              outlineWidth: 0,
              outline: "none",
              boxShadow: "none",
            }}
            className={
              (shouldDisable ? "text-grayscale-lightest cursor-not-allowed" : "") +
              " " +
              TextAreaStyle
            }
            disabled={disabled}
            {...props}
            {...(placeholderFromField ? { placeholder: placeholderInput!.value } : {})}
            rows={3} // Mininum # of rows to set min-height
            ref={ref}
            /**
             * Not going to spread `input` to ensure that this component
             * doesn't get unnecessary props. Things like `onBlur`, `onChange`,
             * `onFocus`, etc. should be explicitly passed.
             */
            onChange={input.onChange}
            value={input.value}
            onFocus={onFocus}
            onBlur={onBlur}
            aria-label={label}
          />
        )
      case LabeledTextFieldType.CODE: {
        return (
          <TextCodeFieldInput
            input={input}
            {...props}
            {...(placeholderFromField ? { placeholder: placeholderInput!.value } : {})}
            autoCompeteFunction={autoCompeteFunction!}
            ref={ref as Ref<ReactCodeMirrorRef>}
            aria-label={label}
            prefix={prefix}
            onFocus={onFocus}
            onBlur={onBlur}
          />
        )
      }
      case LabeledTextFieldType.NORMAL:
      default: {
        return (
          <StyledInput
            className={shouldDisable ? "text-grayscale-lightest cursor-not-allowed" : ""}
            disabled={disabled}
            {...props}
            {...(placeholderFromField ? { placeholder: placeholderInput!.value } : {})}
            ref={ref}
            aria-label={label}
            {...input}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !submitOnEnter) {
                e.preventDefault()
              }
            }}
            /**
             * If the below `onFocus` props is needed then be sure to
             * comment out the `{...input}` prop spread and explicitly
             * set the `value`, `onChange` and `onFocus` props.
             */
            // onFocus={onFocus} // <- uncomment this if ever needed
          />
        )
      }
    }
  }
)

LabeledOrValueTextFieldInputElement.displayName = "LabeledOrValueTextFieldInputElement"

export const LabeledTextField = forwardRef<HTMLInputElement, LabeledTextFieldProps>(
  (
    {
      inputType = LabeledTextFieldType.NORMAL,
      submitOnEnter = true,
      warningText,
      helpText,
      disabled,
      name,
      label,
      className,
      outerProps,
      autoCompeteFunction,
      nullOnEmpty = false,
      isFlushLabel = false,
      toolTipWidth,
      value,
      isBoldWhiteLabel = false,
      ...props
    },
    ref
  ) => {
    const {
      input,
      meta: { touched, error, submitError, submitting },
      extra: { isDisabled },
      ...rest
    } = useEnhancedField(name, {
      parse: (value) => {
        if (props.type === "number") {
          return Number(value)
        }
        if (nullOnEmpty) {
          return value === "" ? null : value
        }
        return value
      },
      allowNull: nullOnEmpty,
    })

    const normalizedError = Array.isArray(error) ? error.join(", ") : error || submitError
    // const normalizedError = Array.isArray(error) ? error.join(", ") : "Invalid"

    const shouldDisable = !!(isDisabled || disabled)

    useEffect(() => {
      if (!!value && input.value !== value) {
        input.onChange(value)
      }
    }, [input, value])

    return (
      <div className={`relative mb-3 -ml-1 ${className || ""}`} {...outerProps}>
        <FieldLabel htmlFor={name}>
          <div className="flex flex-row  items-center">
            {helpText ? (
              <div className="relative">
                <STooltip content={helpText} width="240px" placement="top-start">
                  <span className={isBoldWhiteLabel ? "text-white font-bold" : ""}>{label}</span>
                </STooltip>
              </div>
            ) : (
              <span className={isBoldWhiteLabel ? "text-white font-bold" : ""}>{label}</span>
            )}
            {warningText ? (
              <div className="relative">
                <STooltip content={warningText} type="warning" width="240px" placement="top-start">
                  <span>{label}</span>
                </STooltip>
              </div>
            ) : null}
          </div>
        </FieldLabel>
        <LabeledOrValueTextFieldInputElement
          inputType={inputType}
          shouldDisable={shouldDisable}
          disabled={submitting || shouldDisable}
          autoCompeteFunction={autoCompeteFunction}
          input={input}
          label={label}
          props={props}
          submitOnEnter={submitOnEnter}
          ref={ref}
        />
        {touched && normalizedError && (
          <FormError hasLabel={label === "" ? false : true} normalizedError={normalizedError} />
        )}
      </div>
    )
  }
)

LabeledTextField.displayName = "LabeledTextField"

const dateFromInput = (input: any) => {
  let date: Date | undefined = undefined
  if (typeof input === "string") {
    date = dateFromString(input)
  } else if (input instanceof Date) {
    date = input
  }
  return date
}

const dateStringFromDate = (date: Date) => {
  return date.toISOString().split("T")[0]
}

const dateFromString = (dateString: string) => {
  const date = new Date(dateString)
  return isValidDate(date) ? date : undefined
}

const isValidDate = (date: Date | string | undefined | null): date is Date => {
  return !!date && !isNaN(new Date(date).getTime())
}

const dateStringFromInput = (input: any) => {
  let date = dateFromInput(input)
  return date ? dateStringFromDate(date) : ""
}

export const LabeledDateField = forwardRef<HTMLInputElement, LabeledTextFieldProps>(
  (
    {
      name,
      label,
      className,
      outerProps,
      disabled,
      value,
      helpText,
      nullOnEmpty = false,
      ...props
    },
    ref
  ) => {
    const {
      input,
      meta: { touched, error, submitError, submitting },
      extra: { isDisabled },
      ...rest
    } = useEnhancedField(name, {
      allowNull: nullOnEmpty,
    })

    const normalizedError = Array.isArray(error) ? error.join(", ") : error || submitError
    const shouldDisable = !!(isDisabled || disabled)

    const renderedValue = dateStringFromInput(input.value)
    return (
      <div className={`relative mb-3 -ml-1 ${className || ""}`} {...outerProps}>
        <FieldLabel htmlFor={name}>
          {helpText ? (
            <div className="relative">
              <STooltip content={helpText} width="240px" placement="top-start">
                <span>{label}</span>
              </STooltip>
            </div>
          ) : (
            <span>{label}</span>
          )}
        </FieldLabel>
        <input
          type="date"
          {...input}
          className="rounded-lg bg-grayscale-lightest bg-opacity-10 border border-white border-opacity-10 focus:outline-none focus:border-solid focus:border-opacity-25 hover:border-opacity-25 py-2 px-3 w-full text-white"
          style={{ colorScheme: "dark" }}
          value={renderedValue}
          disabled={submitting || shouldDisable}
          // make sure the value is always a date
          onChange={(e) => {
            console.log("[!] e.target.value", e.target.value)
            if (e) {
              const date = dateFromString(e.target.value)
              input.onChange(date)
            }
          }}
          ref={ref}
          {...props}
        />
        {touched && normalizedError && (
          <FormError hasLabel={!!label} normalizedError={normalizedError} />
        )}
      </div>
    )
  }
)

LabeledDateField.displayName = "LabeledDateField"

export const ValueTextField = forwardRef<
  HTMLInputElement,
  Omit<LabeledTextFieldProps, "name"> & {
    postfix: string
    placeholderFromField?: string
    helpText?: string
    onClickValue?: ({ name }: { name: string }) => void
    onFocus?: () => void
    toolTipWidth?: string
  }
>(
  (
    {
      inputType = LabeledTextFieldType.NORMAL,
      disabled,
      prefix,
      autoCompeteFunction,
      placeholderFromField,
      helpText,
      postfix,
      onClickValue,
      outerProps,
      onFocus,
      onBlur,
      label,
      toolTipWidth,
      ...props
    },
    ref
  ) => {
    const {
      input,
      meta: { touched, error, submitError, submitting },
      extra: { isDisabled },
      ...rest
    } = useEnhancedField(`${prefix}.${postfix}`, {
      parse: props.type === "number" ? Number : undefined,
    })

    const { input: keyInput } = useField(`${prefix}.key`)
    const { input: placeholderInput } = useField(
      placeholderFromField ? placeholderFromField : `${prefix}.key`
    )

    const normalizedError = Array.isArray(error) ? error.join(", ") : error || submitError
    const shouldDisable = !!(isDisabled || disabled)
    const warningText = null

    return (
      <div className={`relative`} {...outerProps}>
        <FieldLabel>
          <div className="flex flex-row  items-center">
            <span>{!!label.length ? label : dashedToTitle(keyInput.value)}</span>
            {helpText ? (
              <div className="relative">
                <STooltip content={helpText} width="240px" placement="top-start">
                  <span>{label}</span>
                </STooltip>
              </div>
            ) : (
              <span>{label}</span>
            )}
            {warningText ? (
              <div className="relative">
                <STooltip content={warningText} type="warning" width="240px" placement="top-start">
                  <span>{label}</span>
                </STooltip>
              </div>
            ) : null}
          </div>
        </FieldLabel>
        <LabeledOrValueTextFieldInputElement
          inputType={inputType}
          shouldDisable={shouldDisable}
          disabled={submitting || shouldDisable}
          input={input}
          autoCompeteFunction={autoCompeteFunction}
          placeholderFromField={placeholderFromField}
          placeholderInput={placeholderInput}
          props={props}
          ref={ref}
          prefix={prefix}
          onFocus={onFocus}
          onBlur={onBlur}
        />
        {touched && normalizedError && (
          <FormError hasLabel={true} normalizedError={normalizedError} />
        )}
      </div>
    )
  }
)

ValueTextField.displayName = "ValueTextField"

export default LabeledTextField
